import {
  ShieldCheck, Target, Sparkles, LayoutList, Mic2, Flag, Lock, Pencil, BookLock, Users, Info,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip'
import { CollapsibleSection } from '@/components/ui/collapsible-section'

// The four scoring dimensions Carlos uses (chief-supervise-message system prompt).
// Definitions are the reader-facing summaries of each dimension's rubric.
const DIMENSIONS = [
  {
    key: 'relevance',
    label: 'Relevancia',
    icon: Target,
    help: 'Qué tan pertinente es el mensaje para ESTE prospecto: problema de pagos concreto, señal usada, encaje con su realidad. Genérico puntúa bajo.',
  },
  {
    key: 'quality',
    label: 'Calidad',
    icon: Sparkles,
    help: 'Solidez del copy: claridad, credibilidad, peer o dato verificado, sin promesas imposibles ni relleno.',
  },
  {
    key: 'structure',
    label: 'Estructura',
    icon: LayoutList,
    help: 'Forma correcta del touch: hook, cuerpo y una sola pregunta calibrada como CTA; longitud y formato propios del canal.',
  },
  {
    key: 'voice',
    label: 'Voz',
    icon: Mic2,
    help: 'Voz de Director Comercial: sin urgencia falsa, sin AI-tells (“Hope this finds you”), sin tono agresivo.',
  },
] as const

// ── Read-only global doctrine (mirrors chief-supervise-message, not editable) ──
// The 4 dimensions above ARE part of the doctrine; below are the invariants Carlos
// enforces regardless of the per-step calibration: the risk triggers (any one
// escalates), the structural checks, and the anti-fabrication customer library.

// Risk triggers — the RISK TRIGGERS block of the Carlos V14 system prompt.
const RISK_TRIGGERS: { key: string; label: string; help: string }[] = [
  { key: 'competitor_aggressive', label: 'Competidor agresivo', help: 'Menospreciar a Stripe/Adyen/Checkout/Braintree por nombre. Se posiciona como orquestación POR ENCIMA, nunca "en contra".' },
  { key: 'claims_undefensible', label: 'Números imposibles', help: 'Cifras físicamente absurdas: lift de aprobación >20%, ahorro MDR >300bps, recovery >90%, "10x", "cero declines", "100% uptime".' },
  { key: 'tone_pushy', label: 'Tono agresivo / urgencia falsa', help: 'Urgencia desesperada, FOMO, culpabilización, "última oportunidad", "tiempo limitado".' },
  { key: 'yuno_as_replacement', label: 'Yuno como reemplazo', help: 'Posicionar a Yuno como REEMPLAZO del PSP actual. Debe ser complementario/aditivo ("no tienes que arrancar Stripe").' },
  { key: 'pricing_comparison', label: 'Comparación de precios', help: 'Discutir pricing/MDR vs competidores en cold email (etapa 2+, nunca etapa 1).' },
  { key: 'amateur_vocab', label: 'Vocabulario amateur', help: 'Confundir gateway/processor/acquirer/PSP/orquestador (p. ej. llamar a Yuno un acquirer).' },
  { key: 'content_offensive', label: 'Contenido ofensivo', help: 'Implica que la empresa está fallando; tono condescendiente o paternalista.' },
  { key: 'regulatory_blindness', label: 'Ceguera regulatoria', help: 'Brasil sin Pix, EU sin PSD2/SCA, India sin tokenización RBI, México sin CoDi/SPEI.' },
  { key: 'persona_mismatch', label: 'Persona equivocada', help: 'Vocabulario que no matchea el título (MDR a un CTO, webhook reliability a un CFO).' },
  { key: 'false_scarcity_or_urgency', label: 'Escasez / urgencia falsa', help: '"Cupos limitados", "termina el viernes", "solo esta semana" — mata la tasa de respuesta.' },
  { key: 'guilt_trip', label: 'Culpabilización', help: '"Te escribimos 3 veces", "si no me respondes", "último intento".' },
  { key: 'angle_duplication', label: 'Ángulo duplicado', help: 'Repite la MISMA capacidad, peer o número que un touch anterior de la cadencia. Day 9 (síntesis) está exento.' },
]

// Structural checks — the structure_breakdown of the Carlos output.
const STRUCTURE_CHECKS: { key: string; label: string }[] = [
  { key: 'hook_pattern_correct', label: 'Hook correcto para el paso (observación insider, no genérica)' },
  { key: 'painted_problem_before_value', label: 'Pinta el problema antes de la propuesta de valor' },
  { key: 'spear_pattern', label: 'Patrón SPEAR (problema → posicionamiento → capacidad → peer → pregunta)' },
  { key: 'single_question_cta', label: 'Una sola pregunta calibrada como CTA (no varias)' },
  { key: 'reference_threading', label: 'Hilo coherente con touches previos de la cadencia' },
  { key: 'pronoun_lock', label: 'Consistencia de persona / pronombres' },
  { key: 'vocabulary_lock', label: 'Vocabulario del rol (auth rate, BIN routing, webhook…)' },
  { key: 'channel_coherence', label: 'Formato y largo propios del canal (email vs DM vs invite)' },
]

// Anti-fabrication library — the ONLY customers citable in cold email (V14).
const CITABLE_WITH_QUOTE: string[] = [
  'Rappi', 'inDrive', 'Livelo', 'Reserva', 'Open English', 'Viva Aerobus',
]
const CITABLE_NO_QUOTE: string[] = [
  'Uber', 'McDonald’s', 'Avianca', 'Xcaret', 'Smartfit', 'SpaceX',
]

interface CarlosNumbers {
  threshold?: number | null
  maxAttempts?: number | null
  minAcceptable?: number | null
}

function fmt(n: number | null | undefined, decimals = 1): string {
  if (n == null || Number.isNaN(n)) return '—'
  return n.toFixed(decimals)
}

// ────────────────────────────────────────────────────────────────────────────
// CarlosExplainer — the fixed banner that explains HOW Carlos scores, above the
// per-step rubric table (PolicySection). The four dimensions + the calibration
// numbers (threshold / max attempts / min-score) are shown from the real org
// globals when passed; risk-triggers always win regardless of score. It also
// states the editable/read-only frontier: calibration is tunable; identity and
// anti-fabrication doctrine are read-only invariants.
// ────────────────────────────────────────────────────────────────────────────
export function CarlosExplainer({ threshold, maxAttempts, minAcceptable }: CarlosNumbers = {}) {
  return (
    <Card className="border-primary/20 bg-primary/[0.03]">
      <CardContent className="space-y-4 pt-6">
        {/* Headline */}
        <div className="flex items-start gap-2">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <div>
            <h3 className="text-base font-semibold leading-tight">Cómo califica Carlos</h3>
            <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
              Carlos es el validador que revisa los mensajes que llegan a la capa de scoring. Puntúa en
              <strong> cuatro dimensiones</strong> de 0 a 10 y toma el <strong>promedio</strong>.
            </p>
          </div>
        </div>

        {/* Four dimensions */}
        <TooltipProvider delayDuration={200}>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {DIMENSIONS.map((d) => {
              const Icon = d.icon
              return (
                <Tooltip key={d.key}>
                  <TooltipTrigger asChild>
                    <div className="flex cursor-help items-center gap-2 rounded-md border bg-background px-3 py-2">
                      <Icon className="h-4 w-4 shrink-0 text-primary" />
                      <span className="text-sm font-medium">{d.label}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs text-xs">
                    {d.help}
                  </TooltipContent>
                </Tooltip>
              )
            })}
          </div>
        </TooltipProvider>

        {/* How the decision is made */}
        <p className="text-sm text-muted-foreground">
          Exige un promedio <strong>≥ {fmt(threshold)}</strong> (umbral por defecto de la org — cada
          paso puede tener el suyo) para aprobar y enviar. Si no llega, pide{' '}
          <strong>regenerar</strong> hasta <strong>{fmt(maxAttempts, 0)}</strong> veces con feedback
          prescriptivo; al agotar intentos manda el mejor si alcanza el{' '}
          <strong>piso {fmt(minAcceptable)}</strong>, o <strong>salta el touch</strong> para no
          quemar el lead.
        </p>

        {/* Carlos honest note — LLM scoring only runs on burn-in or a failed deterministic gate */}
        <div className="flex items-start gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Hoy Carlos solo puntúa un mensaje cuando (a) el paso está en modo <strong>burn-in</strong>{' '}
            o (b) un chequeo determinista previo falla. Si el mensaje pasa los gates deterministas,
            sale sin puntuación LLM (es el caso de la mayoría de los envíos). La rúbrica de abajo
            define cómo puntuaría cuando corra.
          </span>
        </div>

        {/* Risk triggers always win */}
        <div className="flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/[0.06] px-3 py-2">
          <Flag className="mt-0.5 h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />
          <p className="text-xs text-red-700 dark:text-red-300">
            <strong>Las banderas rojas ganan siempre.</strong> Un tono agresivo, nombrar a un
            competidor, una promesa imposible o suplantar la propuesta de valor frenan el mensaje
            sin importar el puntaje.
          </p>
        </div>

        {/* Editable / read-only frontier */}
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="flex items-start gap-2 rounded-md border bg-background px-3 py-2">
            <Pencil className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
            <p className="text-xs text-muted-foreground">
              <Badge variant="outline" className="mr-1 border-emerald-500/30 text-emerald-700 dark:text-emerald-300 text-[10px] font-normal">
                Editable
              </Badge>
              La <strong>calibración</strong>: umbral, reintentos, piso y la instrucción de calidad
              por paso (abajo).
            </p>
          </div>
          <div className="flex items-start gap-2 rounded-md border bg-background px-3 py-2">
            <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <p className="text-xs text-muted-foreground">
              <Badge variant="secondary" className="mr-1 text-[10px] font-normal">
                Solo lectura
              </Badge>
              La <strong>identidad</strong>: las 4 dimensiones, las banderas rojas y la
              anti-fabricación (los clientes citables) son invariantes de marca.
            </p>
          </div>
        </div>

        {/* ── Global doctrine (reference, read-only) ─────────────────────────── */}
        <CollapsibleSection
          title="Doctrina de Carlos (referencia, no editable)"
          description="Las 4 dimensiones, los risk triggers, los chequeos estructurales y los clientes citables — invariantes de marca."
          icon={<BookLock className="h-4 w-4" />}
          badge="solo lectura"
        >
          <div className="space-y-4 text-xs">
            {/* Dimensions recap */}
            <div>
              <p className="mb-1.5 font-semibold text-foreground">Las 4 dimensiones (0–10, promedio)</p>
              <ul className="space-y-1">
                {DIMENSIONS.map((d) => (
                  <li key={d.key} className="flex gap-1.5 text-muted-foreground">
                    <d.icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                    <span><strong className="text-foreground">{d.label}:</strong> {d.help}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Risk triggers */}
            <div>
              <p className="mb-1.5 flex items-center gap-1.5 font-semibold text-foreground">
                <Flag className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
                Risk triggers — cualquiera frena el mensaje sin importar el puntaje
              </p>
              <ul className="grid gap-1 sm:grid-cols-2">
                {RISK_TRIGGERS.map((t) => (
                  <li key={t.key} className="rounded-md border bg-background px-2 py-1.5 text-muted-foreground">
                    <span className="font-medium text-foreground">{t.label}</span>
                    {' — '}{t.help}
                    <code className="ml-1 text-[10px] opacity-60">{t.key}</code>
                  </li>
                ))}
              </ul>
            </div>

            {/* Structure checks */}
            <div>
              <p className="mb-1.5 flex items-center gap-1.5 font-semibold text-foreground">
                <LayoutList className="h-3.5 w-3.5 text-primary" />
                Chequeos estructurales (structure_breakdown)
              </p>
              <ul className="grid gap-1 sm:grid-cols-2">
                {STRUCTURE_CHECKS.map((c) => (
                  <li key={c.key} className="flex gap-1.5 text-muted-foreground">
                    <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-primary/60" />
                    {c.label}
                  </li>
                ))}
              </ul>
            </div>

            {/* Anti-fabrication customer library */}
            <div>
              <p className="mb-1.5 flex items-center gap-1.5 font-semibold text-foreground">
                <Users className="h-3.5 w-3.5 text-primary" />
                Anti-fabricación: los ÚNICOS clientes citables
              </p>
              <p className="mb-1.5 text-muted-foreground">
                Citar cualquier otro cliente con una métrica dispara un risk trigger. Con cita pública
                verificada se puede atribuir un quote; sin cita pública SOLO se dice "corre sobre la
                capa de orquestación de Yuno" (nunca inventar ejecutivo ni métrica).
              </p>
              <div className="flex flex-wrap gap-1">
                {CITABLE_WITH_QUOTE.map((c) => (
                  <Badge key={c} variant="outline" className="border-emerald-500/30 text-emerald-700 dark:text-emerald-300 text-[10px] font-normal">
                    {c} · quote público
                  </Badge>
                ))}
                {CITABLE_NO_QUOTE.map((c) => (
                  <Badge key={c} variant="secondary" className="text-[10px] font-normal">
                    {c} · sin quote
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </CollapsibleSection>
      </CardContent>
    </Card>
  )
}
